import { NgClass, NgIf, UpperCasePipe } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChip } from '@angular/material/chips';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { get } from 'lodash';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { EDialogWidth } from '../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../core/validators/trimmed-required.validator';
import { DatatableComponent } from '../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../shared/components/datatable/datatable.interface';
import { DuplicateDialogComponent } from '../../../shared/components/duplicate-dialog/duplicate-dialog.component';
import { ErrorMessagesComponent } from '../../../shared/components/error-messages/error-messages.component';
import { FilterCardComponent } from '../../../shared/components/filter-card/filter-card.component';
import { IBaseDatatableState } from '../../../state/base-datatable-store.service';
import { FormUtilitiesService } from '../../../utilities/form-utilities.service';
import { HttpUtilitiesService } from '../../../utilities/http-utilities.service';
import { BaseCrudPageComponent } from '../../app/base-crud-page.component';
import { EFieldType } from '../fields-field-sets/fields/fields.interface';
import { WorkInstructionEditorComponent } from './work-instruction-editor/work-instruction-editor.component';
import {
  IStepForm,
  TWorkInstructionStepFormUpdate,
} from './work-instruction-editor/work-instruction-steps/work-instruction-steps.interface';
import {
  IWorkInstruction,
  IWorkInstructionEditor,
  TAddWorkInstruction,
  TFormControlWorkInstruction,
  TWorkInstructionBulkEditForm,
  TWorkInstructionBulkEditRequest,
  TWorkInstructionForm,
} from './work-instructions.interface';
import { WorkInstructionsStore } from './work-instructions.store';

@Component({
  imports: [
    DatatableComponent,
    ErrorMessagesComponent,
    FilterCardComponent,
    MatButton,
    MatCard,
    MatCardContent,
    MatCheckbox,
    MatChip,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    NgIf,
    ReactiveFormsModule,
    TranslateModule,
    UpperCasePipe,
    NgClass,
    WorkInstructionEditorComponent,
  ],
  providers: [WorkInstructionsStore, HttpUtilitiesService],
  selector: 'app-work-instructions',
  standalone: true,
  styleUrl: './work-instructions.component.scss',
  templateUrl: './work-instructions.component.html',
})
export class WorkInstructionsComponent
  extends BaseCrudPageComponent<IWorkInstruction, IBaseDatatableState<IWorkInstruction>>
  implements OnInit
{
  @ViewChild('addEditFormDialog') addEditFormDialog!: TemplateRef<HTMLElement>;
  @ViewChild('workInstructionEditorDialog') workInstructionEditorDialog!: TemplateRef<HTMLElement>;

  public selectedWorkInstruction: IWorkInstructionEditor | undefined;

  public datatableConfigurations: IDatatableConfiguration<IWorkInstruction> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.bulk,
    duplicateScope: ETableCrudScope.single,
    editScope: ETableCrudScope.bulk,
  };

  public readonly datatableColumns: IDatatableColumn<IWorkInstruction>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
    { id: 'isActive', isSortable: true },
  ];

  constructor(
    public readonly store: WorkInstructionsStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfWorkInstruction',
      multiContext: 'field.workInstructions',
      nameProperty: 'name',
      singleContext: 'field.workInstruction',
    });
  }

  public readonly addEditForm: FormGroup<TFormControlWorkInstruction> = new FormGroup({
    isActive: new FormControl(true),
    name: new FormControl('', [
      Validators.required,
      TrimmedRequiredValidator(),
      Validators.minLength(1),
      Validators.maxLength(50),
    ]),
  });
  public editedFormOfSteps: IStepForm[] = [];

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        datatable: this.datatableParams$.pipe(filter(Boolean)),
        filterCard: this.filterCardData$.pipe(filter(Boolean)),
      }).subscribe(({ datatable, filterCard }) => {
        this.store.loadItems({
          fields: ['name', 'isActive'],
          filters: [
            ...(filterCard.status !== null && filterCard.status !== undefined
              ? [{ field: 'isActive', ids: [filterCard.status] }]
              : []),
          ],
          limit: datatable.limit,
          page: datatable.page,
          sort: [datatable.sort],
          ...(datatable.search ? { search: { searchedFields: ['name'], searchText: datatable.search } } : {}),
        });
      }),
    );
  }

  public initializeAdd(): void {
    this.setupForAdd();
    this.addEditForm.enable();

    this.addEditForm.reset({
      isActive: true,
      name: '',
    });

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public initializeEdit(): void {
    if (!this.selectedRows().length) {
      return;
    }

    this.addEditForm.enable();
    this.setupForEdit();

    if (this.isBulkEdit()) {
      this.addEditForm.disable();
      this.addEditForm.reset();
    } else {
      const [item] = this.selectedRows();

      this.addEditForm.reset({
        isActive: item.isActive,
        name: item.name,
      });
    }

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      return;
    }

    if (!this.isEditDialog()) {
      this.store.createOne(this.formatOnePayload(this.addEditForm.value as TWorkInstructionForm));
    } else if (this.isBulkEdit()) {
      this.store.updateMany(this.formatBulkEditPayload(this.addEditForm.value as TWorkInstructionForm));
    } else {
      const [item] = this.selectedRows();
      this.store.updateOne({ dto: this.formatOnePayload(this.addEditForm.value as TWorkInstructionForm), id: item.id });
    }

    this.matDialog.closeAll();
  }

  private formatOnePayload(item: TWorkInstructionForm): TAddWorkInstruction {
    return {
      isActive: item.isActive,
      name: FormUtilitiesService.trimIfString(item.name),
    };
  }

  protected readonly EFieldType = EFieldType;

  public openWorkInstructionEditor(id: number, name: string): void {
    this.selectedWorkInstruction = { id, name };
    this.editedFormOfSteps = [];
    this.matDialog.open(this.workInstructionEditorDialog, WorkInstructionEditorComponent.dialogConfig);
  }

  public getEditedForms($event: IStepForm[]): void {
    this.editedFormOfSteps = $event;
  }

  public onSubmit(): void {
    if (!this.editedFormOfSteps.length) {
      this.matDialog.closeAll();

      return;
    }

    const workInstructionId: number | undefined = this.selectedWorkInstruction?.id;
    const workInstructionIdIsValid: boolean = workInstructionId === get(this.editedFormOfSteps, '0.workInstructionId');

    if (!workInstructionId || !workInstructionIdIsValid) {
      return;
    }

    const updatedSteps: TWorkInstructionStepFormUpdate[] = this.editedFormOfSteps.map((step: IStepForm) => {
      return {
        form: step.form,
        guideId: workInstructionId,
        id: step.stepId,
      };
    });

    this.store.bulkUpdateWorkInstructionSteps(updatedSteps);
  }

  public duplicateWorkInstruction(): void {
    if (!this.selectedRow()) {
      return;
    }

    this.matDialog
      .open(DuplicateDialogComponent, { data: { context: this.context.singleContext }, width: EDialogWidth.xSmall })
      .afterClosed()
      .subscribe((modalResult: string | undefined) => {
        if (!modalResult) {
          return;
        }

        this.store.duplicateOne({ id: this.selectedRow()!.id, name: modalResult });
      });
  }

  private formatBulkEditPayload(bulkEditForm: TWorkInstructionBulkEditForm): TWorkInstructionBulkEditRequest[] {
    return this.selectedRows().map((oldData: IWorkInstruction) => {
      return {
        id: oldData.id,
        ...('isActive' in bulkEditForm ? { isActive: Boolean(bulkEditForm.isActive) } : {}),
      };
    });
  }
}
